import { useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useHotkeys } from '@mantine/hooks';
import {
  AppShell,
  Group,
  Image,
  Box,
  Paper,
  Text,
  Divider,
  HoverCard,
  TextInput,
  Burger,
  NavLink,
  ActionIcon,
  ScrollArea,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconSearch,
  IconBrandAsana,
  IconAddressBook,
  IconBuildingStore,
  IconMap2,
  IconSocial,
  IconReportMoney,
  IconChevronLeft,
  IconUserCircle,
  IconMoon,
  IconSun,
  IconReceiptDollar,
  IconCash,
  IconTags,
  IconMailOpened,
} from '@tabler/icons-react';
import logoDark from '../assets/logo.png';
import logoLight from '../assets/logo-light.png';
import { useDisclosure } from '@mantine/hooks';
import { useTheme } from '../context/ThemeProvider.tsx';
import useStaffInfo from '../hooks/staff/use-staff-info.ts';

const NAV_BAR_WIDTH: number = 240;
const XL_WIDTH: number = 32;

export default function AppLayout() {
  const location = useLocation();
  const [isBurgerOpened, { toggle: toggleBurger }] = useDisclosure(true);
  const { colorScheme, toggleColorScheme } = useTheme();
  const { staff, isLoading: isStaffInfoLoading } = useStaffInfo();
  const [menuKeyword, setMenuKeyword] = useState<string>('');
  const menuSearchInputRef = useRef<HTMLInputElement>(null);

  useHotkeys([['mod+K', () => menuSearchInputRef.current?.focus()]]);

  if (isStaffInfoLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  const oldAdminUrl: string = import.meta.env.VITE_BASE_URL;
  const menus = [
    {
      label: '任務管理',
      icon: <IconBrandAsana size={18} />,
      path: '/missions',
      resource: 'MISSION',
    },
    {
      label: '業者管理',
      icon: <IconAddressBook size={18} />,
      path: '/partners',
      resource: 'PARTNER',
    },
    {
      label: '商店管理',
      icon: <IconBuildingStore size={18} />,
      path: '/stores',
      resource: 'STORE',
    },
    {
      label: '圖資管理',
      icon: <IconMap2 size={18} />,
      path: '/places',
      resource: 'PLACE',
    },
    {
      label: '品牌管理',
      icon: <IconSocial size={18} />,
      path: '/brands',
      resource: 'BRAND',
    },
    {
      label: '場站付款方式管理',
      icon: <IconCash size={18} />,
      path: '/payment-methods',
      resource: 'PAYMENT_METHOD',
    },
    {
      label: '標籤管理',
      icon: <IconTags size={18} />,
      path: '/tags',
      resource: 'TAG',
    },
    {
      label: '財務管理',
      icon: <IconReportMoney size={18} />,
      paths: [
        {
          label: '待確認對帳單',
          path: '/billing/unconfirmed',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '已確認對帳單',
          path: '/billing/confirmed',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '匯款單列表',
          path: '/remittance-instructions',
          resource: 'MAAS_BILLING_REMITTANCE_INSTRUCTION',
        },
        {
          label: '歷史對帳單',
          path: '/billing/history',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
        {
          label: '廠商匯款帳號',
          path: '/site-providers',
          resource: 'MAAS_BILLING_SITE_PROVIDER',
        },
        {
          label: '對帳單設定',
          path: '/billing/settings',
          resource: 'MAAS_BILLING_PARKING_BILLING_SETTING',
        },
      ],
    },
    {
      label: '訊息管理',
      icon: <IconMailOpened size={18} />,
      paths: [
        {
          label: '訊息管理',
          path: '/messages',
          resource: 'MAAS_BILLING_PARKING_BILLING',
        },
      ],
    },
    {
      label: '查繳任務',
      icon: <IconReceiptDollar size={18} />,
      resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
      paths: [
        {
          label: '查繳任務',
          path: '/tickets/pending',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
        {
          label: '歷史任務',
          path: '/tickets/history',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
        {
          label: '通行費申請單',
          path: '/tickets/enrollments',
          resource: 'COCKPIT_ETC_AUTO_PAY_TICKET',
        },
      ],
    },
  ]
    .filter((item) => {
      // 根據擁有的權限篩選淺層資源
      if (item.path) {
        return (
          staff.permissions[item.resource]?.read ||
          staff.permissions[item.resource]?.write
        );
      } else {
        return item.paths.some(
          (nestedItem) =>
            staff.permissions[nestedItem.resource]?.read ||
            staff.permissions[nestedItem.resource]?.write,
        );
      }
    })
    .map((item) => {
      // 根據擁有的權限決定暴露的深層資源
      if (item.path) {
        return item;
      } else {
        return {
          ...item,
          paths: item.paths.filter(
            (nestedItem) =>
              staff.permissions[nestedItem.resource]?.read ||
              staff.permissions[nestedItem.resource]?.write,
          ),
        };
      }
    })
    .filter(
      (item) =>
        // 根據搜尋欄位篩選淺層資源
        item.label.includes(menuKeyword) ||
        item.paths?.some((nestedItem) =>
          nestedItem.label.includes(menuKeyword),
        ),
    )
    .map((item) => {
      // 根據搜尋欄位決定暴露的深層資源
      if (item.path) {
        return item;
      } else {
        return {
          ...item,
          paths: item.paths.filter((nestedItem) =>
            nestedItem.label.includes(menuKeyword),
          ),
        };
      }
    });

  return (
    <AppShell
      navbar={{
        width: NAV_BAR_WIDTH,
        breakpoint: 'sm',
        collapsed: { mobile: !isBurgerOpened, desktop: false },
      }}
    >
      <AppShell.Navbar withBorder style={{ height: '100vh' }}>
        <Group px="md" pt="xs" justify="start">
          <Burger
            opened={isBurgerOpened}
            onClick={toggleBurger}
            hiddenFrom="sm"
            size="sm"
          />
          <Box component={Link} to="/">
            <Image
              src={colorScheme === 'light' ? logoDark : logoLight}
              alt="Logo"
              height={50}
              px="md"
              fit="contain"
            />
          </Box>
        </Group>

        <TextInput
          px="md"
          pb={10}
          ref={menuSearchInputRef}
          leftSection={<IconSearch size={20} />}
          placeholder="ctrl + K 以搜尋"
          value={menuKeyword}
          onChange={(e) => setMenuKeyword(e.currentTarget.value)}
        />

        <ScrollArea px="md" pb="xs" style={{ flex: 1 }}>
          {menus.map((item) => {
            if (item.path) {
              return (
                <NavLink
                  key={item.path}
                  leftSection={item.icon}
                  label={item.label}
                  component={Link}
                  to={item.path}
                  variant="light"
                  active={location.pathname.startsWith(item.path)}
                />
              );
            }

            const nestedNavLinks = item.paths?.map((nestedItem) => (
              <NavLink
                key={nestedItem.path}
                label={nestedItem.label}
                component={Link}
                to={nestedItem.path}
                variant="light"
                active={location.pathname.startsWith(nestedItem.path)}
              />
            ));

            return (
              <NavLink
                label={item.label}
                leftSection={item.icon}
                childrenOffset={28}
                variant="subtle"
                defaultOpened
                active={nestedNavLinks.some((link) => link.props.active)}
              >
                {nestedNavLinks}
              </NavLink>
            );
          })}
        </ScrollArea>

        <Box px="md" pb={4}>
          <NavLink
            label="點擊返回舊後台"
            leftSection={<IconChevronLeft size={18} />}
            key={oldAdminUrl}
            component={Link}
            to={oldAdminUrl}
            variant="light"
          />
        </Box>

        <Divider mx="xs" />
        <HoverCard shadow="md" openDelay={200} position="right">
          <HoverCard.Target>
            <Group
              gap={4}
              p="xs"
              h={48}
              align="center"
              justify="space-between"
              style={(theme) => ({
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor:
                    colorScheme === 'dark'
                      ? theme.colors.dark[5]
                      : theme.colors.gray[1],
                },
              })}
            >
              <ActionIcon
                w={22}
                h={22}
                variant="light"
                radius="xl"
                style={{ border: '1px solid lightgray' }}
              >
                <IconUserCircle size={18} />
              </ActionIcon>

              <Box w={100}>
                <Text
                  fz={14}
                  c={colorScheme === 'dark' ? 'white' : 'black'}
                  truncate="end"
                >
                  {staff.name}
                </Text>
              </Box>

              <Group align="center" flex={1} justify="flex-end">
                <ActionIcon
                  variant="transparent"
                  c={colorScheme === 'light' ? 'black' : 'white'}
                  onClick={() => {
                    toggleColorScheme();
                  }}
                >
                  {(colorScheme === 'light' && <IconMoon size={20} />) || (
                    <IconSun size={20} />
                  )}
                </ActionIcon>
              </Group>
            </Group>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Paper>
              <Text fz={12} c="dimmed">
                email
              </Text>
              <Text fz={12}>{staff.email}</Text>
            </Paper>
          </HoverCard.Dropdown>
        </HoverCard>
      </AppShell.Navbar>
      <AppShell.Main
        pl={NAV_BAR_WIDTH + XL_WIDTH}
        pr="xl"
        py="lg"
        display="flex"
        className="justify-center"
      >
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
